import { Graphics, Text, Sprite, Texture } from 'pixi.js'
import { Entity } from './Entity'
import { BuildingType, BUILDING_TYPES, getBuildingType } from '../data/buildings'
import { Faction, getFaction } from '../data/factions'
import { AssetLoader } from '../engine/AssetLoader'

export interface TrainingQueueItem {
  unitType: string
  progress: number
  totalTime: number
}

export class Building extends Entity {
  buildingType: BuildingType
  faction: Faction

  tileX: number
  tileY: number
  tileSize: number

  // Construction
  constructionProgress: number = 100  // 0-100
  isConstructed: boolean = true

  // Training
  trainingQueue: TrainingQueueItem[] = []
  rallyPoint: { x: number; y: number } | null = null

  // Combat (for towers)
  attackCooldown: number = 0

  // Visual
  private bodyGraphic: Graphics
  private iconText: Text
  private progressBar: Graphics
  private sprite: Sprite | null = null
  private useSprite: boolean = false

  constructor(
    typeId: string,
    tileX: number,
    tileY: number,
    tileSize: number,
    isPlayer: boolean,
    factionId: string,
    startConstructed: boolean = true
  ) {
    const buildingType = getBuildingType(typeId)
    const faction = getFaction(factionId)

    // Calculate position (center of building)
    const worldX = tileX * tileSize + (buildingType.size * tileSize) / 2
    const worldY = tileY * tileSize + (buildingType.size * tileSize) / 2

    super(worldX, worldY, buildingType.hp, isPlayer)

    this.buildingType = buildingType
    this.faction = faction
    this.tileX = tileX
    this.tileY = tileY
    this.tileSize = tileSize

    this.constructionProgress = startConstructed ? 100 : 0
    this.isConstructed = startConstructed

    // Create visuals
    this.bodyGraphic = new Graphics()
    this.bodyGraphic.zIndex = 0
    this.container.addChild(this.bodyGraphic)

    this.iconText = new Text({
      text: this.getIcon(),
      style: {
        fontSize: Math.min(buildingType.size * 20, 40),
        fill: 0xffffff
      }
    })
    this.iconText.anchor.set(0.5, 0.5)
    this.iconText.zIndex = 1
    this.container.addChild(this.iconText)

    this.progressBar = new Graphics()
    this.progressBar.zIndex = 2
    this.container.addChild(this.progressBar)

    // Try to load sprite
    const texture = AssetLoader.getBuildingTexture(typeId, factionId)
    if (texture) {
      this.sprite = new Sprite(texture)
      this.sprite.anchor.set(0.5, 0.5)
      // Scale sprite to fit building size
      const targetSize = buildingType.size * tileSize
      this.sprite.scale.set(targetSize / texture.width, targetSize / texture.height)
      this.sprite.zIndex = 0
      this.container.addChild(this.sprite)
      this.useSprite = true
      // Hide fallback graphics when using sprite
      this.bodyGraphic.visible = false
      this.iconText.visible = false
    }

    // Selection ring is larger for buildings
    this.drawSelectionRing(buildingType.size * tileSize / 2 + 4, 0x00ff00)
    this.updateHealthBar()
    this.render()
  }

  private getIcon(): string {
    switch (this.buildingType.id) {
      case 'DATACENTER': return '⬢'
      case 'COMPUTE_FARM': return '▣'
      case 'DATA_MINE': return '▤'
      case 'POWER_PLANT': return '⚡'
      case 'BARRACKS': return '⚔'
      case 'RESEARCH_LAB': return '◎'
      case 'FACTORY': return '✪'
      case 'TOWER': return '▲'
      case 'WALL': return '▬'
      case 'WONDER': return '✦'
      default: return '□'
    }
  }

  update(dt: number): void {
    if (this.isDead) return

    // Update construction
    if (!this.isConstructed) {
      // Construction progress is updated externally
      if (this.constructionProgress >= 100) {
        this.isConstructed = true
        this.constructionProgress = 100
      }
    }

    // Update training
    if (this.isConstructed && this.trainingQueue.length > 0) {
      const current = this.trainingQueue[0]
      const productionMod = this.faction.bonuses.productionSpeed || 1
      current.progress += (dt / current.totalTime) * 100 * productionMod

      // Note: When progress >= 100, Game class handles spawning and removing from queue
    }

    // Update attack cooldown (for towers)
    if (this.attackCooldown > 0) {
      this.attackCooldown -= dt
    }
  }

  // Queue a unit for training
  queueUnit(unitType: string, buildTime: number): boolean {
    if (!this.isConstructed) return false
    if (!this.buildingType.canTrain?.includes(unitType)) return false

    this.trainingQueue.push({
      unitType,
      progress: 0,
      totalTime: buildTime
    })

    return true
  }

  // Cancel training
  cancelTraining(index: number = -1): TrainingQueueItem | null {
    if (index < 0) {
      return this.trainingQueue.pop() || null
    }
    if (index >= this.trainingQueue.length) return null
    return this.trainingQueue.splice(index, 1)[0]
  }

  // Get current training progress (0-100)
  getTrainingProgress(): number {
    if (this.trainingQueue.length === 0) return 0
    return this.trainingQueue[0].progress
  }

  // Check if building can attack
  canAttack(): boolean {
    return this.isConstructed &&
           this.buildingType.attack !== undefined &&
           this.attackCooldown <= 0
  }

  // Perform attack (returns damage dealt)
  performAttack(): number {
    if (!this.canAttack()) return 0

    this.attackCooldown = 1 / (this.buildingType.attackSpeed || 1)
    return this.buildingType.attack || 0
  }

  // Get attack range in pixels
  getAttackRange(): number {
    return (this.buildingType.range || 0) * this.tileSize
  }

  // Advance construction (called by workers)
  advanceConstruction(amount: number): void {
    if (this.isConstructed) return

    const buildMod = this.faction.bonuses.buildSpeed || 1
    this.constructionProgress += amount / buildMod

    if (this.constructionProgress >= 100) {
      this.constructionProgress = 100
      this.isConstructed = true
    }
  }

  render(): void {
    const size = this.buildingType.size * this.tileSize

    // If using sprite, handle sprite-based rendering
    if (this.useSprite && this.sprite) {
      // Tint sprite red for enemies
      if (!this.isPlayer) {
        this.sprite.tint = 0xff6666
      }

      // Make sprite visible based on construction progress
      if (!this.isConstructed) {
        this.sprite.alpha = 0.3 + (this.constructionProgress / 100) * 0.7
      } else {
        this.sprite.alpha = 1
      }

      // Selection glow (using bodyGraphic for overlay effects)
      this.bodyGraphic.visible = true
      this.bodyGraphic.clear()
      if (this.isSelected) {
        this.bodyGraphic.rect(-size / 2 - 4, -size / 2 - 4, size + 8, size + 8)
        this.bodyGraphic.stroke({ width: 3, color: this.faction.glowColor, alpha: 0.7 })
      }
    } else {
      // Fallback to geometric rendering
      this.bodyGraphic.clear()

      // Building body
      const color = this.isPlayer ? this.faction.primaryColor : 0xe74c3c

      // Main rectangle
      this.bodyGraphic.rect(-size / 2, -size / 2, size, size)
      this.bodyGraphic.fill({ color })

      // Border
      this.bodyGraphic.rect(-size / 2, -size / 2, size, size)
      this.bodyGraphic.stroke({
        width: 3,
        color: this.isPlayer ? 0xffffff : 0x000000
      })

      // Construction overlay
      if (!this.isConstructed) {
        const constructedHeight = (this.constructionProgress / 100) * size
        this.bodyGraphic.rect(
          -size / 2,
          -size / 2,
          size,
          size - constructedHeight
        )
        this.bodyGraphic.fill({ color: 0x000000, alpha: 0.5 })
      }

      // Selection highlight
      if (this.isSelected) {
        this.bodyGraphic.rect(-size / 2 - 2, -size / 2 - 2, size + 4, size + 4)
        this.bodyGraphic.stroke({ width: 2, color: this.faction.glowColor, alpha: 0.5 })
      }
    }

    // Update progress bar (training/construction) - same for both modes
    this.progressBar.clear()

    const barWidth = size
    const barHeight = 4
    const barY = size / 2 + 4

    if (!this.isConstructed) {
      // Construction progress
      this.progressBar.rect(-barWidth / 2, barY, barWidth, barHeight)
      this.progressBar.fill({ color: 0x333333 })
      this.progressBar.rect(-barWidth / 2, barY, barWidth * (this.constructionProgress / 100), barHeight)
      this.progressBar.fill({ color: 0xffd700 })
    } else if (this.trainingQueue.length > 0) {
      // Training progress
      this.progressBar.rect(-barWidth / 2, barY, barWidth, barHeight)
      this.progressBar.fill({ color: 0x333333 })
      this.progressBar.rect(-barWidth / 2, barY, barWidth * (this.getTrainingProgress() / 100), barHeight)
      this.progressBar.fill({ color: 0x3498db })
    }

    // Update health bar position
    this.healthBar.position.y = -size / 2 - 12
  }

  // Override health bar for buildings (wider)
  protected updateHealthBar(): void {
    this.healthBar.clear()

    const size = this.buildingType.size * this.tileSize
    const barWidth = size
    const barHeight = 5
    const x = -barWidth / 2
    const y = 0

    // Background
    this.healthBar.rect(x, y, barWidth, barHeight)
    this.healthBar.fill({ color: 0x333333 })

    // Health
    const hpPercent = this.hp / this.maxHp
    const healthColor = hpPercent > 0.5 ? 0x2ecc71 :
                       hpPercent > 0.25 ? 0xf39c12 : 0xe74c3c

    this.healthBar.rect(x, y, barWidth * hpPercent, barHeight)
    this.healthBar.fill({ color: healthColor })
  }

  // Get the distance from a point to the nearest edge of this building
  distanceToEdge(pointX: number, pointY: number): number {
    const halfSize = (this.buildingType.size * this.tileSize) / 2

    // Clamp the point to the building's bounding box
    const closestX = Math.max(this.x - halfSize, Math.min(pointX, this.x + halfSize))
    const closestY = Math.max(this.y - halfSize, Math.min(pointY, this.y + halfSize))

    // Calculate distance from point to closest point on building edge
    const dx = pointX - closestX
    const dy = pointY - closestY
    return Math.sqrt(dx * dx + dy * dy)
  }
}
